import React, { useState, useEffect, useCallback } from 'react';
import {
  FaUserPlus, FaEdit, FaTrash, FaSearch, FaKey, FaUserShield, FaUsers, FaIdBadge,
} from 'react-icons/fa';
import api, { errorMessage, getRole } from '../api';
import { ROLES, ROLE_LABELS, MANAGEMENT, hasRole } from './attendance/roles';
import {
  PageShell, Card, Badge, Button, Modal, Field, inputClass, Alert, Loading,
  Table, formatDate,
} from './attendance/ui';

const BLANK = {
  employeeId: '', name: '', email: '', phone: '', gender: '', dateOfBirth: '',
  address: '', emergencyContactName: '', emergencyContactPhone: '',
  department: '', location: '', shift: '', supervisor: '', position: '',
  employmentType: 'full_time', hireDate: '', contractEndDate: '',
  status: 'active', role: ROLES.EMPLOYEE, biometricId: '', annualLeaveDays: 20,
};

/** Staff profiles: create, edit, deactivate, and issue system logins. */
const EmployeeManagement = () => {
  const role = getRole();
  const canEdit = hasRole(role, MANAGEMENT);

  const [staff, setStaff] = useState([]);
  const [options, setOptions] = useState({ departments: [], locations: [], shifts: [] });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ department: '', location: '', role: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [editing, setEditing] = useState(null);      // employee being edited, or 'new'
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const [staffRes, departmentRes, locationRes, shiftRes] = await Promise.all([
        api.get('employees/'),
        api.get('departments/'),
        api.get('locations/'),
        api.get('shifts/'),
      ]);
      setStaff(staffRes.data);
      setOptions({
        departments: departmentRes.data,
        locations: locationRes.data,
        shifts: shiftRes.data,
      });
    } catch (err) {
      setError(errorMessage(err, 'Could not load staff records.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const next = staff.length + 1;
    setForm({ ...BLANK, employeeId: `EMP${String(next).padStart(3, '0')}` });
    setEditing('new');
  };

  const openEdit = (employee) => {
    setForm({
      ...BLANK,
      ...Object.fromEntries(
        Object.keys(BLANK).map((key) => [key, employee[key] ?? BLANK[key]]),
      ),
    });
    setEditing(employee);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    // Empty strings would fail date/relation validation, so drop them.
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, value]) => value !== '' && value !== null),
    );
    try {
      if (editing === 'new') {
        await api.post('employees/', payload);
        setNotice(`${form.name} added to the staff register.`);
      } else {
        await api.put(`employees/${editing.id}/`, payload);
        setNotice(`${form.name} updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the staff record.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (employee) => {
    if (!window.confirm(`Delete ${employee.name}? Their attendance history goes with them.`)) return;
    try {
      await api.delete(`employees/${employee.id}/`);
      setNotice(`${employee.name} removed.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the record.'));
    }
  };

  const createAccount = async (employee) => {
    try {
      const { data } = await api.post(`employees/${employee.id}/create-account/`, {});
      setCredentials({ name: employee.name, ...data, action: 'created' });
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not create the account.'));
    }
  };

  const resetPassword = async (employee) => {
    if (!window.confirm(`Reset the password for ${employee.name}?`)) return;
    try {
      const { data } = await api.post(`employees/${employee.id}/reset-password/`, {});
      setCredentials({ name: employee.name, ...data, action: 'reset' });
    } catch (err) {
      setError(errorMessage(err, 'Could not reset the password.'));
    }
  };

  const visible = staff.filter((employee) => {
    const needle = search.trim().toLowerCase();
    const matchesSearch = !needle ||
      `${employee.name} ${employee.employeeId} ${employee.email || ''} ${employee.position || ''}`
        .toLowerCase().includes(needle);
    return matchesSearch
      && (!filters.department || employee.department === filters.department)
      && (!filters.location || employee.location === filters.location)
      && (!filters.role || employee.role === filters.role)
      && (!filters.status || employee.status === filters.status);
  });

  const update = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  if (loading) return <PageShell title="Staff Management"><Loading /></PageShell>;

  return (
    <PageShell
      title="Staff Management"
      subtitle={`${staff.length} staff record(s)`}
      actions={canEdit && <Button onClick={openNew}><FaUserPlus /> Add staff</Button>}
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <Card className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-1">
            <Field label="Search">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input value={search} onChange={(event) => setSearch(event.target.value)}
                  placeholder="Name, ID, position…" className={`${inputClass} pl-9`} />
              </div>
            </Field>
          </div>
          <Field label="Department">
            <select value={filters.department} className={inputClass}
              onChange={(event) => setFilters({ ...filters, department: event.target.value })}>
              <option value="">All</option>
              {options.departments.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="Location">
            <select value={filters.location} className={inputClass}
              onChange={(event) => setFilters({ ...filters, location: event.target.value })}>
              <option value="">All</option>
              {options.locations.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="Role">
            <select value={filters.role} className={inputClass}
              onChange={(event) => setFilters({ ...filters, role: event.target.value })}>
              <option value="">All</option>
              {Object.entries(ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select value={filters.status} className={inputClass}
              onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
              <option value="">All</option>
              {['active', 'inactive', 'suspended', 'terminated'].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <Card title={<span className="flex items-center gap-2"><FaUsers className="text-gray-400" /> Staff register ({visible.length})</span>}>
        <Table
          columns={['Staff ID', 'Name', 'Department', 'Location', 'Position', 'Role',
            'Biometric ID', 'Hired', 'Status', 'Login', canEdit ? 'Actions' : '']}
          rows={visible}
          empty="No staff match these filters."
          renderRow={(employee) => (
            <tr key={employee.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="py-3 pr-4 font-mono text-xs text-gray-600">{employee.employeeId}</td>
              <td className="py-3 pr-4">
                <div className="font-medium text-gray-800 whitespace-nowrap">{employee.name}</div>
                <div className="text-xs text-gray-500">{employee.email || '—'}</div>
              </td>
              <td className="py-3 pr-4 whitespace-nowrap">{employee.department || '—'}</td>
              <td className="py-3 pr-4 whitespace-nowrap">
                {employee.location || '—'}
                {employee.locationType && (
                  <span className="block text-xs text-gray-400 capitalize">{employee.locationType}</span>
                )}
              </td>
              <td className="py-3 pr-4 whitespace-nowrap">{employee.position || '—'}</td>
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{ROLE_LABELS[employee.role] || employee.role}</td>
              <td className="py-3 pr-4 font-mono text-xs">{employee.biometricId || '—'}</td>
              <td className="py-3 pr-4 whitespace-nowrap text-xs">{employee.hireDate ? formatDate(employee.hireDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
              <td className="py-3 pr-4"><Badge value={employee.status} /></td>
              <td className="py-3 pr-4 text-xs">
                {employee.hasAccount
                  ? <span className="text-green-600 font-semibold">{employee.username}</span>
                  : <span className="text-gray-400">none</span>}
              </td>
              {canEdit && (
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(employee)} title="Edit"
                      className="p-2 text-gray-400 hover:text-[#15A857] hover:bg-green-50 rounded-lg">
                      <FaEdit />
                    </button>
                    {employee.hasAccount ? (
                      <button onClick={() => resetPassword(employee)} title="Reset password"
                        className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
                        <FaKey />
                      </button>
                    ) : (
                      <button onClick={() => createAccount(employee)} title="Create login"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <FaUserShield />
                      </button>
                    )}
                    <button onClick={() => remove(employee)} title="Delete"
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                      <FaTrash />
                    </button>
                  </div>
                </td>
              )}
            </tr>
          )}
        />
      </Card>

      {/* Create / edit */}
      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add staff member' : `Edit ${editing?.name || ''}`} size="max-w-4xl">
        <form onSubmit={save} className="space-y-6">
          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Identity</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Staff ID *">
                <input required value={form.employeeId} onChange={update('employeeId')} className={inputClass} />
              </Field>
              <Field label="Full name *">
                <input required value={form.name} onChange={update('name')} className={inputClass} />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} onChange={update('email')} className={inputClass} />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={update('phone')} className={inputClass} />
              </Field>
              <Field label="Gender">
                <select value={form.gender} onChange={update('gender')} className={inputClass}>
                  <option value="">Not specified</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Date of birth">
                <input type="date" value={form.dateOfBirth} onChange={update('dateOfBirth')} className={inputClass} />
              </Field>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Assignment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Department">
                <select value={form.department} onChange={update('department')} className={inputClass}>
                  <option value="">Unassigned</option>
                  {options.departments.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                </select>
              </Field>
              <Field label="Branch / location">
                <select value={form.location} onChange={update('location')} className={inputClass}>
                  <option value="">Unassigned</option>
                  {options.locations.map((item) => (
                    <option key={item.id} value={item.name}>{item.name} ({item.type})</option>
                  ))}
                </select>
              </Field>
              <Field label="Shift" hint="Drives late, overtime and working-hour calculations.">
                <select value={form.shift} onChange={update('shift')} className={inputClass}>
                  <option value="">Institution default</option>
                  {options.shifts.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
                </select>
              </Field>
              <Field label="Position">
                <input value={form.position} onChange={update('position')} className={inputClass} />
              </Field>
              <Field label="Supervisor" hint="Staff ID or name of the reporting supervisor.">
                <input value={form.supervisor} onChange={update('supervisor')} className={inputClass} />
              </Field>
              <Field label="System role">
                <select value={form.role} onChange={update('role')} className={inputClass}>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Employment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Employment type">
                <select value={form.employmentType} onChange={update('employmentType')} className={inputClass}>
                  <option value="full_time">Full time</option>
                  <option value="part_time">Part time</option>
                  <option value="contract">Contract</option>
                  <option value="temporary">Temporary</option>
                </select>
              </Field>
              <Field label="Hire date">
                <input type="date" value={form.hireDate} onChange={update('hireDate')} className={inputClass} />
              </Field>
              <Field label="Contract ends">
                <input type="date" value={form.contractEndDate} onChange={update('contractEndDate')} className={inputClass} />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={update('status')} className={inputClass}>
                  {['active', 'inactive', 'suspended', 'terminated'].map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <Field label="Biometric ID" hint="The user number enrolled on the attendance machine.">
                <input value={form.biometricId} onChange={update('biometricId')} className={inputClass} />
              </Field>
              <Field label="Annual leave days">
                <input type="number" min="0" value={form.annualLeaveDays}
                  onChange={update('annualLeaveDays')} className={inputClass} />
              </Field>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Address">
                <input value={form.address} onChange={update('address')} className={inputClass} />
              </Field>
              <Field label="Emergency contact">
                <input value={form.emergencyContactName} onChange={update('emergencyContactName')} className={inputClass} />
              </Field>
              <Field label="Emergency phone">
                <input value={form.emergencyContactPhone} onChange={update('emergencyContactPhone')} className={inputClass} />
              </Field>
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <Button type="submit" disabled={saving} className="flex-1">
              <FaIdBadge /> {saving ? 'Saving…' : editing === 'new' ? 'Add staff member' : 'Save changes'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* Credentials handout */}
      <Modal isOpen={!!credentials} onClose={() => setCredentials(null)}
        title={credentials?.action === 'reset' ? 'Password reset' : 'Login created'} size="max-w-md">
        {credentials && (
          <div className="space-y-4">
            <Alert tone="warning">
              Give these details to {credentials.name} now — the password is not stored
              in readable form and cannot be shown again.
            </Alert>
            <div className="bg-gray-50 rounded-xl p-4 font-mono text-sm space-y-2">
              <div><span className="text-gray-500">Username:</span> <strong>{credentials.username}</strong></div>
              <div><span className="text-gray-500">Password:</span> <strong>{credentials.password}</strong></div>
            </div>
            <Button className="w-full" onClick={() => setCredentials(null)}>Done</Button>
          </div>
        )}
      </Modal>
    </PageShell>
  );
};

export default EmployeeManagement;
